import React, { useRef, useEffect } from 'react';

interface VideoPlayerProps {
  className?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ className = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Get Supabase URL from environment or use default
  const supabaseUrl =
    process.env.REACT_APP_SUPABASE_URL || 'https://placeholder.supabase.co';

  // Construct the video URL from Supabase storage
  const videoUrl = `${supabaseUrl}/storage/v1/object/public/static/demo.mp4`;

  // Detect if user agent is a bot
  const isBot =
    typeof navigator !== 'undefined' &&
    (navigator.userAgent.toLowerCase().includes('bot') ||
      navigator.userAgent.toLowerCase().includes('crawler') ||
      navigator.userAgent.toLowerCase().includes('spider'));

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Handle video metadata loading
    const handleLoadedMetadata = () => {
      // Ensure video duration is properly loaded
      if (video.duration && isFinite(video.duration)) {
        // Duration is loaded and valid
      }
    };

    // Handle video errors - don't report to Slack (bots, crawlers, ad blockers,
    // and unsupported browsers often fail; these errors are not actionable)
    const handleError = () => {
      // Silently ignore - video errors are expected for many crawlers
    };

    // Handle video waiting (buffering)
    const handleWaiting = () => {
      // Video is buffering
    };

    // Handle video can play
    const handleCanPlay = () => {
      // Video is ready to play
    };

    // Handle video ended - check if it actually reached the end
    const handleEnded = () => {
      // Only trigger if video actually reached the end
      if (video.currentTime >= video.duration - 0.1) {
        // Video actually ended
      }
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('error', handleError);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('error', handleError);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('ended', handleEnded);
    };
  }, [videoUrl]);

  return (
    <div
      className={`relative w-full h-full rounded-2xl overflow-hidden shadow-2xl ${className}`}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        className='w-full h-full object-cover'
        controls
        autoPlay={!isBot}
        muted
        playsInline
        preload={isBot ? 'none' : 'auto'}
        crossOrigin='anonymous'
      />
    </div>
  );
};

export default VideoPlayer;
